import React, { useMemo, useRef, useState } from 'react';
import { HwDialog } from '../../views/HomeView/widgets/HwDialog';
import { useHabitDisciplinePrompt, type NutritionTickRequest } from '../../../stores/habitDisciplinePromptStore';
import { useHabitStore } from '../../../stores/habitStore';
import { useNutritionStore } from '../../../stores/nutritionStore';
import { usePlanState } from '../../../stores/nutritionPlanStore';
import { readableOn } from '../../../utils/colorText';
import { planDayOn, plannedFor } from '../../../utils/nutritionPlan';
import { occasionForSlotOn } from '../../../utils/nutritionLink';
import { scaleNutrients } from '../../../utils/nutrition';
import { dayLabel, longDate, portionText } from './nutritionFormat';
import { NutrientLine, SaveError } from './nutritionUi';
import { AddFoodDialog } from './AddFoodDialog';
import { toLocalYMD } from '../../../utils/hydration';
import { useNutritionAccent } from './nutritionPalette';

/**
 * ⭐ Global host for a Nutrition-linked tick (phase 3b-2, owner 2026-10-09). Ticking an
 * interval of the eating habit — from ANY screen — parks a request; this asks what was
 * eaten: "Ate as planned · Change meal or portion · Complete habit only" (no plan →
 * "Choose what you ate"). Cancel changes nothing.
 *
 * ONE OPERATION: the food entry and the tick share an operation id, saved food first,
 * then the tick, each WAITED for. A failed food save ticks nothing; a failed tick says the
 * food IS logged and offers Try again — which re-saves the same tick, never a second food
 * entry or completion. Mounted once at the app root.
 * ⭐ A Nutrition dialog, so it wears Nutrition's accent (owner, 2026-10-21) — saffron, copper on
 * light — whatever domain the habit is filed under; red stays for the save that failed.
 */
export function NutritionLogPromptHost() {
  const req = useHabitDisciplinePrompt(s => s.nutrition);
  const clear = useHabitDisciplinePrompt(s => s.clearNutrition);
  if (!req) return null;
  return <NutritionTickDialog key={`${req.habitId}|${req.date}|${req.slotId}`} req={req} onDone={clear} />;
}

type Phase = 'choose' | 'food' | 'saving' | 'tickFailed';

function NutritionTickDialog({ req, onDone }: { req: NutritionTickRequest; onDone: () => void }) {
  const habit = useHabitStore(s => s.habits.find(h => h.id === req.habitId));
  const completeLinked = useHabitStore(s => s.completeLinked);
  const meals = useNutritionStore(s => s.meals);
  const history = useNutritionStore(s => s.history);
  const logFood = useNutritionStore(s => s.logFood);
  const planState = usePlanState();
  const accent = useNutritionAccent();
  const opId = useRef(crypto.randomUUID()).current;

  const [phase, setPhase] = useState<Phase>('choose');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loggedName, setLoggedName] = useState<string | null>(null);

  // The occasion this interval stands for on the plan that date falls on (none → a plain log).
  const occasion = habit ? occasionForSlotOn(habit, req.slotId, planState, req.date) : null;
  const planned = occasion ? plannedFor(planState.entries, occasion.id, req.date, planDayOn(planState, req.date)?.day ?? null) : null;
  const meal = planned ? meals.find(m => m.id === planned.mealId) ?? null : null;
  const already = useMemo(
    () => (occasion ? (history.find(d => d.date === req.date)?.entries ?? []).find(e => e.occasionId === occasion.id) ?? null : null),
    [history, occasion, req.date],
  );
  const what = [req.habitName, req.slotLabel].filter(Boolean).join(' · ');
  const today = toLocalYMD(new Date());
  const day = dayLabel(req.date, today);

  /** The habit half — waited for; a failure keeps the dialog with Try again. */
  const tick = async (food: string | null) => {
    setBusy(true); setError(null);
    const r = await completeLinked(req.habitId, req.date, req.slotId, opId);
    setBusy(false);
    if (r.ok) { onDone(); return; }
    setLoggedName(food);
    setPhase('tickFailed');
  };

  const ateAsPlanned = async () => {
    if (busy || !meal || !planned || !occasion) return;
    setBusy(true); setError(null);
    const r = await logFood(req.date, { meal, portion: planned.portion, occasionId: occasion.id }, { opId, habitAllowsDate: true });
    if (!r.ok) { setBusy(false); setError(r.error); return; }
    await tick(meal.name);
  };

  if (!habit) return null;

  if (phase === 'food') {
    return (
      <AddFoodDialog
        date={req.date}
        dateLabel={day === 'Today' || day === 'Yesterday' ? `${day} · ${longDate(req.date)}` : day}
        color={accent}
        initialMeal={meal && !meal.archivedAt ? meal : undefined}
        initialPortion={planned?.portion ?? 1}
        occasion={occasion ? { id: occasion.id, name: occasion.name, kind: occasion.kind } : undefined}
        logOptions={{ opId, habitAllowsDate: true }}
        onLogged={(_id, name) => { setPhase('saving'); void tick(name); }}
        onClose={() => setPhase(p => (p === 'food' ? 'choose' : p))}
      />
    );
  }

  const ink = readableOn(accent);
  if (phase === 'saving') {
    return (
      <HwDialog title={what || 'Saving'} subtitle="Food logged — ticking the habit…" onClose={() => {}}>
        <p className="text-sm text-muted-foreground" role="status">Saving…</p>
      </HwDialog>
    );
  }
  const primary = 'h-10 sm:h-9 px-3 rounded-lg text-sm font-semibold disabled:opacity-50';
  const secondary = 'h-10 sm:h-9 px-3 rounded-lg border border-border text-sm font-semibold text-foreground disabled:opacity-50';

  if (phase === 'tickFailed') {
    return (
      <HwDialog
        title="Almost done"
        subtitle={what}
        onClose={onDone}
        footer={(
          <>
            <button type="button" onClick={onDone} disabled={busy} className={secondary}>Close</button>
            <button type="button" onClick={() => void tick(loggedName)} disabled={busy} className={primary} style={{ backgroundColor: accent, color: ink }}>{busy ? 'Saving…' : 'Try again'}</button>
          </>
        )}
      >
        <div className="space-y-2 text-sm" role="alert">
          {loggedName
            ? <p className="text-foreground"><span className="font-semibold">“{loggedName}”</span> is logged in Nutrition.</p>
            : null}
          <p className="text-red-600 dark:text-red-400">Ticking “{what}” didn’t save — check your connection. Try again to tick it; nothing will be logged twice.</p>
          {loggedName && <p className="text-xs text-muted-foreground">Closing keeps the food logged and the habit unticked.</p>}
        </div>
      </HwDialog>
    );
  }

  return (
    <HwDialog
      title={what || 'What did you eat?'}
      subtitle={`${day === 'Today' ? 'Today' : longDate(req.date)}${occasion ? ` · ${occasion.name}` : ''} — what did you eat?`}
      onClose={onDone}
      footer={<button type="button" onClick={onDone} disabled={busy} className={secondary}>Cancel</button>}
    >
      <div className="space-y-3">
        {already ? (
          <div className="rounded-lg border border-border p-3 text-sm">
            <p className="text-foreground">Already logged: <span className="font-semibold">{already.name}</span>{typeof already.portion === 'number' ? ` · ${portionText(already.portion)}` : ''}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Tick the habit for it — nothing is logged twice.</p>
          </div>
        ) : planned ? (
          <div className="rounded-lg border border-dashed border-border p-3 text-sm">
            <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: accent }}>Planned</p>
            <p className="text-foreground">{meal ? meal.name : 'A meal that can’t be found'} <span className="text-muted-foreground">· {portionText(planned.portion)}</span></p>
            {meal && <p className="text-xs text-muted-foreground"><NutrientLine nutrients={scaleNutrients(meal.nutrients, planned.portion)} /></p>}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{occasion ? `Nothing is planned for ${occasion.name} that day.` : 'This interval isn’t linked to a meal-plan occasion.'}</p>
        )}

        <div className="flex flex-col gap-2">
          {already ? (
            <button type="button" onClick={() => void tick(null)} disabled={busy} className={primary} style={{ backgroundColor: accent, color: ink }}>{busy ? 'Saving…' : 'Tick the habit'}</button>
          ) : (
            <>
              {meal && planned && (
                <button type="button" onClick={() => void ateAsPlanned()} disabled={busy} className={primary} style={{ backgroundColor: accent, color: ink }}>{busy ? 'Saving…' : 'Ate as planned'}</button>
              )}
              <button type="button" onClick={() => { setError(null); setPhase('food'); }} disabled={busy} className={secondary}>
                {planned ? 'Change meal or portion' : 'Choose what you ate'}
              </button>
              <button type="button" onClick={() => void tick(null)} disabled={busy} className={secondary}>Complete habit only</button>
            </>
          )}
        </div>
        {error && <SaveError message={error} onRetry={meal && planned && !already ? () => void ateAsPlanned() : undefined} />}
      </div>
    </HwDialog>
  );
}
